import { DateTime } from 'luxon'
import { useEffect, useRef } from 'react'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSnackbar } from 'notistack'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { SyncStatus } from '../../DTOs/Sync/SyncDtos'
import { useGlobalModal } from '../Contexts/GlobalModalContext'
import ConflictResolutionModal from './ConflictResolutionModal'
import { cachedMachineName, describeShiftId } from '../../offline/describeShiftId'

// Note: which change an issue is about. Four identical red lines saying "a truck load change" gave the
// crew nothing to act on - not the machine, not the shift, not even when it was logged. The client data
// is what they entered, so name it from there and fall back to the server's copy.
function issueContext(issue: SyncIssue): string {
  const row = (issue.clientData ?? issue.serverState ?? {}) as {
    plantId?: number
    truckId?: number
    plant?: { plantRef?: string }
    loads?: number
    hour?: number
    startTime?: unknown
  }

  const machine = row.plant?.plantRef ?? cachedMachineName(row.plantId ?? row.truckId)
  const shift = describeShiftId(issue.shiftId)
  const detail = row.loads != null ? `${row.loads} loads` : undefined
  const hour = row.hour != null ? `hour ${row.hour}` : undefined
  const logged = issue.createdAt ? DateTime.fromMillis(issue.createdAt).toFormat('HH:mm') : undefined

  const parts = [machine, detail, hour, shift, logged && `logged ${logged}`].filter(Boolean)
  return parts.length ? ` (${parts.join(', ')})` : ''
}

function issueLabel(issue: SyncIssue): string {
  const entityName = issue.entity === 'diggingPattern' ? 'digging location'
    : issue.entity === 'patternBlast' ? 'blast'
    : issue.entity === 'loading' ? 'truck load'
    : issue.entity === 'geoRehandle' ? 'rehandle/crushing entry'
    : issue.entity === 'completeShift' ? 'shift completion'
    : issue.entity
  const about = issueContext(issue)
  if (issue.status === SyncStatus.Conflict) return `A ${entityName} conflict${about}: it was edited by someone else`
  if (issue.status === SyncStatus.ShiftLocked) return `A ${entityName} change${about} was rejected: shift is locked`
  if (issue.status === SyncStatus.Error) return `A ${entityName} change${about} failed: ${typeof issue.serverState === 'string' ? issue.serverState : 'server error'}`
  return `A ${entityName} change${about} couldn't be applied because the record no longer exists on the server (it may have been deleted). Re-enter it if it's still needed, then dismiss this.`
}

async function dismissIssue(id: number) {
  await offlineDb.syncIssues.delete(id)
}

async function dismissAll() {
  await offlineDb.syncIssues.clear()
}

const SyncIssuesBanner = () => {
  const issuesRaw = useLiveQuery(() => offlineDb.syncIssues.orderBy('createdAt').toArray(), [])
  const issues = issuesRaw ?? []
  const { showModal, hideModal } = useGlobalModal()
  const { enqueueSnackbar } = useSnackbar()

  // Note: raise a transient notification when new sync issues are recorded, so a conflict or
  // rejected change is not silent (the banner alone is easy to miss). Skip the first Dexie
  // emission so pre-existing issues restored on load do not fire a notification.
  const prevCountRef = useRef<number>(-1)
  useEffect(() => {
    if (issuesRaw === undefined) return
    const prev = prevCountRef.current
    if (prev >= 0 && issuesRaw.length > prev) {
      const added = issuesRaw.length - prev
      enqueueSnackbar(
        `${added} offline change${added === 1 ? '' : 's'} need${added === 1 ? 's' : ''} your attention. See the sync issues bar at the top of the screen.`,
        { variant: 'warning', autoHideDuration: 8000 }
      )
    }
    prevCountRef.current = issuesRaw.length
  }, [issuesRaw, enqueueSnackbar])

  const openConflictModal = (issue: SyncIssue) => {
    showModal({
      content: () => (
        <ConflictResolutionModal issue={issue} onClose={hideModal} />
      ),
      tailwindMaxWidthClass: 'max-w-2xl',
    })
  }

  if (issues.length === 0) return null

  return (
    <div className="w-full flex flex-col gap-1 px-4 py-2 short:py-1 bg-theme-danger text-theme-primary-contrast text-sm border-b border-theme-danger-hover shrink-0">
      <div className="flex items-center justify-between gap-2 min-h-[1.75rem]">
        <div className="flex items-center gap-2 font-medium">
          <FontAwesomeIcon icon={faTriangleExclamation} className="shrink-0" />
          <span>{issues.length} sync {issues.length === 1 ? 'issue' : 'issues'} need your attention</span>
        </div>
        {issues.length > 1 && (
          <button
            onClick={dismissAll}
            className="shrink-0 rounded-sm border border-white/60 px-3 py-1 text-xs font-semibold text-white hover:bg-white/15 transition"
          >
            Dismiss all
          </button>
        )}
      </div>
      <ul className="flex flex-col gap-0.5 pl-6">
        {issues.map(issue => (
          <li key={issue.id} className="flex items-center justify-between gap-2 min-h-[1.75rem]">
            <span className="text-xs opacity-90">{issueLabel(issue)}</span>
            <div className="flex items-center gap-2 shrink-0">
              {issue.status === SyncStatus.Conflict && (
                <button
                  onClick={() => openConflictModal(issue)}
                  className="shrink-0 rounded-sm bg-white px-3 py-1 text-xs font-bold text-theme-danger hover:bg-white/90 transition"
                >
                  Resolve
                </button>
              )}
              <button
                onClick={() => dismissIssue(issue.id!)}
                className="shrink-0 rounded-sm border border-white/60 px-3 py-1 text-xs font-semibold text-white hover:bg-white/15 transition"
              >
                Dismiss
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default SyncIssuesBanner
